// client/src/user/popups/PopupRenderer.js
import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  IconButton,
  Typography,
  alpha,
  useTheme,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
  Campaign as CampaignIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import CommonDialog from "../../common/CommonDialog";

const PopupRenderer = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);

  const [dialogPopups, setDialogPopups] = useState([]);
  const [topbarPopups, setTopbarPopups] = useState([]);
  const [currentDialogIndex, setCurrentDialogIndex] = useState(0);
  const [dismissedLocal, setDismissedLocal] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("dismissed_popups") || "[]"),
      );
    } catch {
      return new Set();
    }
  });

  const fetchPopups = useCallback(async () => {
    const res = await hitAxios({
      path: "/api/popup/user/active",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) return;

    const all = res.data.data || [];

    // Filter out locally dismissed non-permanent ones
    const visible = all.filter((p) => {
      if (p.is_permanent === "yes") return true;
      return !dismissedLocal.has(p.popup_id);
    });

    setDialogPopups(visible.filter((p) => p.type === "dialog"));
    setTopbarPopups(visible.filter((p) => p.type === "topbar"));
  }, [hitAxios, dismissedLocal]);

  useEffect(() => {
    fetchPopups();
  }, []);

  const dismissPopup = useCallback(
    async (popup) => {
      // Always call server dismiss
      await hitAxios({
        path: "/api/popup/user/dismiss",
        post: true,
        obj: { popup_id: popup.popup_id },
        showLoading: false,
        showSnackbar: false,
      });

      if (popup.is_permanent !== "yes") {
        // Also store locally to avoid re-showing before next fetch
        setDismissedLocal((prev) => {
          const next = new Set(prev);
          next.add(popup.popup_id);
          localStorage.setItem("dismissed_popups", JSON.stringify([...next]));
          return next;
        });
      }
    },
    [hitAxios],
  );

  // ── Dismiss current dialog popup ──────────────────────────────────────────
  const handleCloseDialog = useCallback(async () => {
    const popup = dialogPopups[currentDialogIndex];
    if (!popup) return;

    await dismissPopup(popup);

    if (popup.is_permanent !== "yes") {
      // Move to next or close
      if (currentDialogIndex < dialogPopups.length - 1) {
        setCurrentDialogIndex((i) => i + 1);
      } else {
        setDialogPopups([]);
        setCurrentDialogIndex(0);
      }
    }
    // If permanent — dialog stays open (user must click X each visit, it re-shows next visit)
    // We just close it for this session
    if (popup.is_permanent === "yes") {
      setDialogPopups((prev) =>
        prev.filter((_, i) => i !== currentDialogIndex),
      );
    }
  }, [dialogPopups, currentDialogIndex, dismissPopup]);

  // ── Dismiss topbar ────────────────────────────────────────────────────────
  const handleCloseTopbar = useCallback(
    async (popup) => {
      await dismissPopup(popup);
      setTopbarPopups((prev) =>
        prev.filter((p) => p.popup_id !== popup.popup_id),
      );
    },
    [dismissPopup],
  );

  const currentDialog = dialogPopups[currentDialogIndex] || null;

  return (
    <>
      {/* ── Top Bar Popups ── */}
      {topbarPopups.map((popup) => (
        <TopBarBanner
          key={popup.popup_id}
          popup={popup}
          onClose={() => handleCloseTopbar(popup)}
          theme={theme}
        />
      ))}

      {/* ── Dialog Popup ── */}
      {currentDialog && (
        <CommonDialog
          open={true}
          onClose={handleCloseDialog}
          title={currentDialog.title}
          icon={CampaignIcon}
          maxWidth="sm"
        >
          <Box
            sx={{ mt: 1 }}
            dangerouslySetInnerHTML={{ __html: currentDialog.content }}
          />
          {dialogPopups.length > 1 && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: "block", textAlign: "right", mt: 2 }}
            >
              {currentDialogIndex + 1} / {dialogPopups.length}
            </Typography>
          )}
        </CommonDialog>
      )}
    </>
  );
};

// ── TopBarBanner sub-component ────────────────────────────────────────────────
const TopBarBanner = ({ popup, onClose, theme }) => {
  const [visible, setVisible] = useState(true);

  const bgColor =
    popup.bg_color ||
    (theme.palette.mode === "dark"
      ? theme.palette.primary.dark
      : theme.palette.primary.main);

  const textColor = popup.text_color || "#ffffff";

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          width: "100%",
          bgcolor: bgColor,
          color: textColor,
          px: { xs: 2, sm: 3 },
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexShrink: 0,
          zIndex: 10,
          boxShadow: `0 2px 8px ${alpha(bgColor, 0.4)}`,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "0.82rem",
            lineHeight: 1.5,
            "& a": { color: "inherit", textDecoration: "underline" },
            "& strong": { fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: popup.content }}
        />
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: textColor,
            flexShrink: 0,
            opacity: 0.8,
            "&:hover": { opacity: 1, bgcolor: alpha("#000", 0.1) },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Slide>
  );
};

export default PopupRenderer;
